import React, {useCallback, useRef} from 'react';

import {CommonInputInterface} from '../common-input-interface';
import {useFocus, useValue} from '../input-hooks';
import {Button} from '../button';
import {CrossIcon, SearchIcon} from '../../svg-icon';
import {cls} from '../../../utils';

import css from './search-input.module.css';

interface SearchInputInterface extends CommonInputInterface<string> {
	setRef?: React.RefObject<HTMLInputElement | null>;
	/** обёртка поля: к ней привязывается выпадающий список */
	wrapperRef?: React.RefObject<HTMLDivElement | null>;
	/** что сделать после очистки крестиком; по умолчанию фокус остаётся в поле */
	onClear?: () => void;
}

export type SearchInputProps = SearchInputInterface &
	Omit<React.InputHTMLAttributes<HTMLInputElement>, keyof SearchInputInterface>;

export const SearchInput: React.FC<SearchInputProps> = (props) => {
	const {
		error,
		disabled,
		noButtons,
		buttons,
		className,
		bottomText,
		helperText,
		onChange: _,
		setRef,
		wrapperRef,
		onClear,
		appearance = 'outline',
		size = 'medium',
		shape = 'rounded',
		label,
		disableClear,
		placeholder = 'Поиск',
		...inputProps
	} = props;
	const {hasFocus, onBlur, onFocus} = useFocus(props);
	const {value, onChange, onChangeValue} = useValue(props);

	const defaultRef = useRef<HTMLInputElement>(null);
	const inputRef = setRef || defaultRef;

	const handleClear = useCallback(() => {
		onChangeValue('');
		if (onClear) onClear();
		else inputRef.current?.focus();
	}, [onChangeValue, onClear, inputRef]);

	const clearButton = value && !disabled && !disableClear && (
		<Button
			appearance="subtle"
			shape={shape === 'square' ? 'square' : 'circular'}
			size="small"
			icon={<CrossIcon width={12} height={12} />}
			onClick={handleClear}
			className={css.button}
			aria-label="Очистить"
		/>
	);

	return (
		<div
			ref={wrapperRef}
			className={cls(
				css.search,
				css[appearance],
				css[size],
				shape !== 'rounded' && css[shape],
				hasFocus && css.focused,
				disabled && css.disabled,
				!!error && error !== true && css.hasError,
				className,
			)}
		>
			<SearchIcon className={css.icon} width={16} height={16} />
			<input
				type="search"
				disabled={disabled}
				placeholder={placeholder}
				aria-label={typeof label === 'string' ? label : placeholder}
				className={css.input}
				{...inputProps}
				onChange={onChange}
				onBlur={onBlur}
				onFocus={onFocus}
				ref={inputRef}
				value={value || ''}
			/>
			{!noButtons && clearButton}
			{!noButtons && buttons}
		</div>
	);
};
